import { useCallback, useEffect, useMemo, useState } from "react";

import { PaymentsWalletsService } from "@/modules/dashboard/wallets/services/paymentsWallets.service";
import type { RuntimeWallet } from "@/modules/dashboard/wallets/types/payments-wallets.types";

interface Snapshot {
  key: string;
  wallet: RuntimeWallet | null;
  hasError: boolean;
}

export interface UseSourceWalletBalanceResult {
  wallet: RuntimeWallet | null;
  /** `balance_available` of the wallet the order debits, or null while unknown. */
  available: string | null;
  isLoading: boolean;
  hasError: boolean;
  /** The organization has no wallet in this currency. */
  missing: boolean;
  refresh: () => void;
}

/**
 * The wallet an OTC order debits. Members are pinned server-side; staff pass the organization they picked,
 * and nothing is requested until they do.
 */
export function useSourceWalletBalance(
  currency: string | null,
  organizationId: string | null,
  enabled: boolean,
): UseSourceWalletBalanceResult {
  const key = enabled && currency ? `${organizationId ?? ""}:${currency}` : "";
  const [snapshot, setSnapshot] = useState<Snapshot | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!key || !currency) return;
    let cancelled = false;
    PaymentsWalletsService.list({ currency, limit: 1, ...(organizationId ? { organizationId } : {}) })
      .then(({ data }) => {
        if (cancelled) return;
        const wallet = data.data.find((candidate) => candidate.currency === currency) ?? null;
        setSnapshot({ key, wallet, hasError: false });
      })
      .catch(() => {
        if (!cancelled) setSnapshot({ key, wallet: null, hasError: true });
      });
    return () => {
      cancelled = true;
    };
  }, [key, currency, organizationId, reloadToken]);

  const refresh = useCallback(() => setReloadToken((token) => token + 1), []);
  const current = key && snapshot?.key === key ? snapshot : null;

  return useMemo(
    () => ({
      wallet: current?.wallet ?? null,
      available: current?.wallet?.balance_available ?? null,
      isLoading: !!key && current === null,
      hasError: current?.hasError ?? false,
      missing: current !== null && !current.hasError && current.wallet === null,
      refresh,
    }),
    [current, key, refresh],
  );
}
